import {
  ConfirmModal,
  Intro,
  NFTItem,
  Lottery20220807,
  Empty,
} from "@/components/nft-auth";
import { CollectionTypes } from "constants/collection";
import { lottery20220807 } from "constants/lottery";
import { INft } from "constants/nft-auth";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Head from "next/head";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { authorizationChangeBind, authorizationList } from "../utils/api";

export async function getStaticProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["common", "nftauth"])),
    },
  };
}

export default function NFTAuth() {
  const { enqueueSnackbar } = useSnackbar();
  const { t: nftauthT } = useTranslation("nftauth");
  const [nftList, setNftList] = useState<INft[]>([]);
  const [currentNft, setCurrentNft] = useState<INft>();

  const [showLottery, setShowLottery] = useState(false);
  const [lotteryManualClosed, setLotteryManualClosed] = useState(false);

  function getFetcher(request) {
    return async () => {
      const { data, err } = await request();
      if (err) {
        if (err.response?.data?.code === 2) {
          location.href = "/api/logout";
        }
        enqueueSnackbar("Unable to connect to the network!", {
          variant: "error",
          preventDuplicate: true,
        });
        throw err;
      }
      return { data, err };
    };
  }

  const fetchAuthorizationList = async () => {
    const { data, err } = await getFetcher(authorizationList)();
    console.log("fetchAuthorizationList", { data, err });

    if (err) {
      setNftList([]);
      return;
    }

    setNftList(data?.items ?? []);
  };

  const handleAuth = (nft) => {
    console.log("handleAuthClick", nft);
    setCurrentNft(nft);
  };

  const handleDeauth = async (nft) => {
    console.log("handleDeauth", nft);

    const { data, err } = await authorizationChangeBind({
      tokenId: nft.tokenId,
      contractAddress: nft.contractAddress,
      fromBiliUid: nft.bindUids[0],
      toBiliUid: 0,
    });

    fetchAuthorizationList();

    if (err) {
      let msg = nftauthT("t25");
      if (err.response.data.code === 120000) {
        msg = nftauthT("t23");
      }
      enqueueSnackbar(msg, { variant: "error" });
      return;
    }

    enqueueSnackbar(nftauthT("t19"), { variant: "success" });
    setCurrentNft(null);
  };

  const handleChangeUid = (nft) => {
    console.log("handleChangeUid", nft);
    setCurrentNft(nft);
  };

  const handleUidConfirm = async (uid) => {
    console.log("handleUidConfirm", uid);

    if (!uid?.trim()) {
      enqueueSnackbar(nftauthT("t18"), { variant: "info" });
      return;
    }

    const { data, err } = await authorizationChangeBind({
      tokenId: currentNft.tokenId,
      contractAddress: currentNft.contractAddress,
      fromBiliUid: currentNft.bindUids[0] || 0,
      toBiliUid: uid,
    });

    // console.log("authorizationChangeBind", { data, err });

    fetchAuthorizationList();

    if (err) {
      let msg = nftauthT("t24");
      if (err.response.data.code === 120000) {
        msg = nftauthT("t23");
      }
      enqueueSnackbar(msg, { variant: "error" });
      return;
    }

    enqueueSnackbar(nftauthT("t17"), { variant: "success" });
    setCurrentNft(null);
  };

  const handleConfirmCancel = () => {
    setCurrentNft(null);
  };

  useEffect(() => {
    fetchAuthorizationList();
  }, []);

  useEffect(() => {
    const hasCUP = nftList.some((nft) =>
      [CollectionTypes.cheersUpPeriod, CollectionTypes.cheersUp].includes(
        nft.contractType
      )
    );

    const _showLottery =
      hasCUP &&
      !lotteryManualClosed &&
      dayjs().isAfter(dayjs(lottery20220807.startTime)) &&
      dayjs().isBefore(dayjs(lottery20220807.lotteryTime));

    setShowLottery(_showLottery);
  }, [nftList, lotteryManualClosed]);

  return (
    <>
      <div className="pt-[110px] px-[80px] font-light mb-[226px]">
        <div className="max-w-[1440px] mx-auto relative pt-[80px]">
          <Intro />

          <div className="mb-[32px]">
            <div className="mt-[80px] flex justify-between items-center mb-[16px]">
              <div className="text-[20px] font-black">NFTs</div>
              <div className="text-[12px] font-light text-[#797F87]">
                {nftauthT("t6")}
              </div>
            </div>
            <div className="h-px bg-[#C3D0DF] scale-y-50"></div>
          </div>

          {nftList.length === 0 ? (
            <Empty />
          ) : (
            <div className="flex flex-wrap">
              {nftList.map((nft) => {
                return (
                  <NFTItem
                    key={nft.contractAddress + nft.tokenId}
                    nft={nft}
                    onAuth={handleAuth}
                    onDeauth={handleDeauth}
                    onChangeUid={handleChangeUid}
                  />
                );
              })}
            </div>
          )}
        </div>

        {!!currentNft && (
          <ConfirmModal
            defaultUid={currentNft.bindUids?.[0]}
            onUidConfirm={handleUidConfirm}
            onCancel={handleConfirmCancel}
          />
        )}

        {showLottery && (
          <Lottery20220807
            onClose={() => {
              setLotteryManualClosed(true);
            }}
          />
        )}
      </div>
    </>
  );
}
